/**
 * Inbound-filter patterns for benign, unactionable client errors we never want
 * as Sentry issues: browser-extension / page-translation DOM mutations,
 * aborted/offline fetches, blocked vendor scripts, wallet-extension clipboard
 * lockdowns, scanner URL probes, in-app-browser JS bridges, and Builder CMS /
 * unsupported-browser warnings. See Sentry triage (2026-06).
 *
 * Passed to `ignoreErrors` in `Sentry.init` (see `src/client.tsx`). The SDK
 * tests each pattern against the event message and every exception value with
 * `stringMatchesSomePattern`, so a match anywhere drops the event before it
 * reaches `beforeSend`.
 *
 * Kept here as plain data rather than inline in `Sentry.init` so the list can
 * be exercised against the SDK's real matcher — a suppression list that grows
 * too broad silently loses real errors, which is exactly the failure a test
 * should catch. See `__tests__/sentry-ignore-errors.test.ts`.
 *
 * @alias sentry noise, ignoreErrors, inbound filters, error suppression
 */
export const SENTRY_IGNORE_ERRORS: RegExp[] = [
  // Extension / page-translation reparenting a node React then unmounts.
  /Cannot read properties of null \(reading 'parentNode'\)/,
  // Aborted or offline fetches (navigation away mid-request, flaky network).
  /Load failed/,
  /Failed to fetch/,
  /NetworkError when attempting to fetch resource/,
  /AbortError/,
  /The user aborted a request/,
  // Clerk's script failed to load (ad blockers / slow network / CDN blip).
  /Failed to load Clerk/,
  /failed_to_load_clerk_js_timeout/,
  // Crypto-wallet / SES-lockdown extensions monkeypatching the clipboard.
  /CONTENT_SHELL/,
  // Malformed %-encoded URL probes from security scanners.
  /URI malformed/,
  // Builder CMS: stale content referencing a component + blocked analytics.
  /Could not find a registered component named/,
  /Failed to track/,
  // Firebase messaging on unsupported browsers (also guarded in code).
  /messaging\/unsupported-browser/,
  // In-app-browser JS bridges: Zalo's iOS WKWebView injects a `zaloJSV2`
  // global and its own injected code references it before definition. The
  // error surfaces via `onerror` with our document as the only stack frame,
  // so `denyUrls`/`allowUrls` can't filter it (see DCM-TC).
  /zaloJSV2/,
  // WebGL unavailable on software-rendered / headless clients. The map now
  // degrades gracefully via an error boundary (see coaches/city); drop the
  // residual reports since we can't fix a client's missing GPU.
  /Failed to initialize WebGL/,
  /Could not create a WebGL context/,
  /webglcontextcreationerror/i,
];
